import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import Drawer from '@mui/material/Drawer';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import { SFPalette } from '@/lib/color';
import { ChevronRight } from '@mui/icons-material';
import Stack from '@mui/material/Stack';
import TextFieldWithLoadingButton from '../common/textfield-with-loading-button';
import { inter } from '@/theme';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { sendEmail, sendEmailProps } from '@/api/server/common/sendEmail';
import { createUser } from '@/api/server/user/create';
import ErrorRes from '@/api/server/ErrorRes';
import { toast } from 'react-toastify';
import { useTermsInfo, useJoinInfoActions, useResetPwInfoActions, JoinState, ResetPwState } from '@/store/zustand/store';
import { checkAuthNo, sendEamilAuth } from '@/api/server/common/auth';
import { isPinExceed } from '@/page/my/pin/pinService';
// import { Lang } from '@/api/server/user/create';
dayjs.extend(duration);

// const schema = {
//   code: '',
// };
const schema = Joi.object({
  code: Joi.string().length(5).required(),
});

interface FormInputs {
  code: string;
}

interface SendMailDrawerProps {
  email: string;
  password: string;
  open: boolean;
  type?: 'create' | 'pin' | 'find';
  onClose: () => void;
}

const LIMIT_TIME = 3 * 60 * 1000;

function SendMailDrawer({ email, password, open, type = 'create', onClose }: SendMailDrawerProps) {
  const navigate = useNavigate();
  const termsInfo = useTermsInfo();
  const { setJoinInfo } = useJoinInfoActions();
  const { setResetPwInfo } = useResetPwInfoActions();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
  } = useForm<FormInputs>({
    resolver: joiResolver(schema),
  });

  useEffect(() => {
    if (open) {
      sendEmailForm();
    }
    return () => {};
  }, [open]);
  const sendEmailForm = () => {
    const payload: sendEmailProps = {
      email,
    };
    sendEmail(payload);
  };

  const onSubmit = async (data: FormInputs) => {
    if (type === 'create') {
      await create(data);
    } else if (type === 'pin') {
      await pin(data);
    } else if (type === 'find') {
      await find(data);
    }
  };
  const create = async (data: FormInputs) => {
    const termArry = ['tt01', 'tt02', 'tt03', 'tt04', 'tt06'];
    if (termsInfo.optional) {
      termArry.push('tt05');
    }
    const termsType = termArry;
    const lang = 'KO' as const;
    const payload = {
      email,
      user_pw: password,
      auth_num: data.code,
      terms_type: termsType,
      lang: lang,
    };
    const re = await createUser(payload);
    if (re.ret_code !== 200) {
      toast.error('인증번호가 다릅니다.');
      // ErrorRes({ code: re.ret_code, msg: re.msg });
      return;
    }
    const joinPayload: JoinState = { email };
    setJoinInfo(joinPayload);
    navigate('/registerPin');
  };
  const pin = async (data: FormInputs) => {
    if (isPinExceed()) return;
    const payload = {
      email,
      emailAuthNo: data.code,
    };
    const re = await sendEamilAuth(payload);
    if (re.ret_code !== 200) {
      ErrorRes({ code: re.ret_code, msg: re.msg, isBack: false });
      return;
    }
    onClose();
    toast.success('인증 성공, 핀 번호를 다시 설정해 주세요');
    navigate('/pinComponent', {
      state: {
        pinType: 'reset',
        title: '핀 번호 다시 설정',
        returnUrl: '/authLevel',
        options: {
          ...payload,
        },
      },
    });
  };
  const find = async (data: FormInputs) => {
    const re = await checkAuthNo({ email, email_auth_no: data.code });
    if (re.ret_code !== 200) {
      ErrorRes({ code: re.ret_code, msg: re.msg, isBack: false });
      return;
    }
    const payload: ResetPwState = {
      email,
      emailAuth: data.code,
    };
    setResetPwInfo(payload);
    navigate(`/resetPw`);
  };
  const code = watch('code');

  const [remainingTime, setRemainingTime] = useState(LIMIT_TIME); // 3 minutes in milliseconds

  useEffect(() => {
    if (open) {
      setRemainingTime(LIMIT_TIME);
    }
  }, [open]);

  useEffect(() => {
    const timer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1000; // reduce by one second
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedTime = dayjs.duration(remainingTime).format('mm:ss');

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          p: 3,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 0, top: 0, p: 0 }}>
          <Close />
        </IconButton>
        <Box mb={3}>
          <Typography variant="h5" gutterBottom>
            {email}로<br />
            <b>인증번호</b>가 <b>발송</b>되었습니다!
          </Typography>
          <Typography variant="body2" gutterBottom color={SFPalette.grey4}>
            '메일이 도착하지 않았다면 스팸함을 확인해보세요.'
          </Typography>
          <Typography variant="body2" gutterBottom color={SFPalette.grey4} sx={{ color: 'red' }}>
            ※ 핀번호 5회 오류 입력시에는 24시간 후 메일인증 가능합니다.
          </Typography>
        </Box>
        {/* <Stack direction="row" mb={3.5}>
          <Typography variant="body2" onClick={() => {}} color={SFPalette.grey4}>
            인증 이메일 변경하기
          </Typography>
          <ChevronRight
            sx={{
              color: SFPalette.lightGrey2,
            }}
          />
        </Stack>*/}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.code}
                helperText={errors.code && '인증번호 5자리를 입력하세요'}
                placeholder="인증번호 5자리 입력"
                sx={{
                  mb: 2.5,
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <>
                      <Typography
                        variant="body2"
                        color={SFPalette.shuffleColor2}
                        fontFamily={inter.style.fontFamily}
                        mr={1.5}
                      >
                        {formattedTime}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          ml: 1,
                        }}
                        onClick={() => {
                          setRemainingTime(LIMIT_TIME);
                          sendEmailForm();
                        }}
                      >
                        재전송
                      </Button>
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="contained"
            type="submit"
            disabled={!!errors.code || !isDirty || code.length !== 5}
            size="large"
            fullWidth
          >
            인증하기
          </Button>
        </form>
      </Box>
    </Drawer>
  );
}

export default SendMailDrawer;
